import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ValidacaoDocumentosService } from '@app/features-modules/gestao-de-concurso/core/validacao-documentos.service';
import {
  ESTADO_VALIDACAO_LABEL,
  formatarTamanhoBytes,
  labelTipo,
} from '@app/features-modules/gestao-de-concurso/core/documentos-candidatura.models';

export interface DocumentoPreviewItem {
  id?: string | number;
  candidatoDocumentoId?: string;
  nomeFicheiro: string;
  mimeType?: string;
  tamanho?: number;
  urlPreview?: string;
  linkAnexoPreview?: string;
  tipoDocumento?: string | { codigo?: string; nome?: string };
  tipoDescricao?: string;
  estadoValidacao?: string;
  estadoValidacaoEnum?: number;
  motivoRejeicao?: string | null;
  validado?: boolean;
}

@Component({
  selector: 'app-documento-preview-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, TooltipModule],
  templateUrl: './documento-preview-modal.component.html',
  styleUrl: './documento-preview-modal.component.scss',
})
export class DocumentoPreviewModalComponent implements OnChanges, OnDestroy {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly validacaoService = inject(ValidacaoDocumentosService);

  @Input() visible = false;
  @Input() set visivel(v: boolean) {
    this.visible = v;
  }
  @Input() documentos: DocumentoPreviewItem[] = [];
  @Input() set anexos(a: any[]) {
    this.documentos = a || [];
  }
  @Input() indiceAtual = 0;
  @Input() permitirAcoes = true;
  @Input() processandoValidacao = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() visivelChange = this.visibleChange;
  @Output() indiceAtualChange = new EventEmitter<number>();
  @Output() aprovar = new EventEmitter<DocumentoPreviewItem>();
  @Output() reprovar = new EventEmitter<DocumentoPreviewItem>();

  carregandoBlob = false;
  erroAoCarregar = false;
  blobUrlSafe?: SafeResourceUrl;

  // Zoom da imagem
  nivelZoom = 1;

  readonly formatarTamanho = formatarTamanhoBytes;
  readonly getEstadoBadge = (val?: number) =>
    val ? ESTADO_VALIDACAO_LABEL[val] ?? { label: 'Em Validação', severity: 'warn' } : null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.carregarFicheiroAtual();
    } else if (changes['indiceAtual'] && this.visible) {
      this.carregarFicheiroAtual();
    }
  }

  ngOnDestroy(): void {
    this.liberarBlobUrl();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.visible) return;
    if (event.key === 'ArrowLeft') {
      this.anterior();
    } else if (event.key === 'ArrowRight') {
      this.seguinte();
    }
  }

  get docAtual(): DocumentoPreviewItem | undefined {
    if (!this.documentos || this.documentos.length === 0) return undefined;
    return this.documentos[this.indiceAtual];
  }

  get totalDocs(): number {
    return this.documentos?.length ?? 0;
  }

  get temAnterior(): boolean {
    return this.indiceAtual > 0;
  }

  get temSeguinte(): boolean {
    return this.indiceAtual < this.totalDocs - 1;
  }

  get rotuloTipoAtual(): string {
    const doc = this.docAtual;
    if (!doc) return 'Documento';
    return doc.tipoDescricao || labelTipo(doc.tipoDocumento);
  }

  get isPdf(): boolean {
    const doc = this.docAtual;
    if (!doc) return false;
    const mime = (doc.mimeType || '').toLowerCase();
    const nome = (doc.nomeFicheiro || '').toLowerCase();
    return mime.includes('pdf') || nome.endsWith('.pdf');
  }

  get isImagem(): boolean {
    const doc = this.docAtual;
    if (!doc) return false;
    const mime = (doc.mimeType || '').toLowerCase();
    const nome = (doc.nomeFicheiro || '').toLowerCase();
    return (
      mime.startsWith('image/') ||
      nome.endsWith('.png') ||
      nome.endsWith('.jpg') ||
      nome.endsWith('.jpeg') ||
      nome.endsWith('.webp')
    );
  }

  anterior(): void {
    if (!this.temAnterior) return;
    this.indiceAtual--;
    this.indiceAtualChange.emit(this.indiceAtual);
    this.carregarFicheiroAtual();
  }

  seguinte(): void {
    if (!this.temSeguinte) return;
    this.indiceAtual++;
    this.indiceAtualChange.emit(this.indiceAtual);
    this.carregarFicheiroAtual();
  }

  fechar(): void {
    this.liberarBlobUrl();
    this.visible = false;
    this.visibleChange.emit(false);
  }

  zoomIn(): void {
    if (this.nivelZoom < 3) {
      this.nivelZoom = parseFloat((this.nivelZoom + 0.25).toFixed(2));
    }
  }

  zoomOut(): void {
    if (this.nivelZoom > 0.5) {
      this.nivelZoom = parseFloat((this.nivelZoom - 0.25).toFixed(2));
    }
  }

  resetZoom(): void {
    this.nivelZoom = 1;
  }

  descarregar(): void {
    const doc = this.docAtual;
    if (!doc) return;

    const url = doc.urlPreview || doc.linkAnexoPreview;
    if (url) {
      this.validacaoService.obterDocumentoBlob(url).subscribe({
        next: (blob) => {
          const blobUrl = URL.createObjectURL(blob);
          this.fazerDownloadBlob(blobUrl, doc.nomeFicheiro);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        },
        error: () => {
          // Fallback para download direto caso falhe o blob
          window.open(url, '_blank');
        },
      });
    }
  }

  onAprovar(): void {
    const doc = this.docAtual;
    if (!doc || this.processandoValidacao) return;
    this.aprovar.emit(doc);
  }

  onReprovar(): void {
    const doc = this.docAtual;
    if (!doc || this.processandoValidacao) return;
    this.reprovar.emit(doc);
  }

  private carregarFicheiroAtual(): void {
    this.liberarBlobUrl();
    this.nivelZoom = 1;
    this.erroAoCarregar = false;

    const doc = this.docAtual;
    if (!doc) return;

    const url = doc.urlPreview || doc.linkAnexoPreview;
    if (!url) {
      this.erroAoCarregar = true;
      return;
    }

    if (!this.isPdf && !this.isImagem) {
      this.carregandoBlob = false;
      return;
    }

    this.carregandoBlob = true;
    this.blobUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  aoCarregarFicheiro(): void {
    this.carregandoBlob = false;
    this.erroAoCarregar = false;
  }

  aoFalharCarregamento(): void {
    this.carregandoBlob = false;
    this.erroAoCarregar = true;
  }

  private fazerDownloadBlob(url: string, nome: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = nome || 'documento';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private liberarBlobUrl(): void {
    this.blobUrlSafe = undefined;
  }
}
